import { useEffect, useState } from 'react'
import { api } from './api'
import { meldeErfolg } from './Erfolgsstelle'
import { fachlicherSatz } from './Fehlersatz'
import { Hinweisfenster } from './Nachfrage'
import { useDarf } from './Rechte'
import { anzahlwort, t } from './sprache'
import { Loeschfenster } from './Stammdaten'
import type { Loeschauftrag, Loeschlage } from './typen'

/**
 * **Position löschen … und Konto löschen … im Bericht** (2.1 Abschnitt 3.6,
 * R1705, R1707) — einmal in der Anwendung, für jedes Berichtsbild.
 *
 * Der Auftrag vom 2026-10-14: *„Anstelle von ‚Zeile löschen', bitte umbenennen
 * in ‚Konto löschen' / ‚Position löschen' / ‚Referenz löschen', je nachdem auf
 * welchen Typ das Kontextmenü geöffnet wurde."* In Bilanz und GuV gab es bis
 * dahin keinen Löschpunkt; gegen die Historie geprüft, es gab ihn nie.
 *
 * **Gelöscht wird wie in den Stammdaten** (entschieden am 2026-10-14): Was
 * verwendet wurde, wird stillgelegt, was nie verwendet wurde, verschwindet —
 * dasselbe Fenster, dieselbe Stelle. **Eine Position, unter der etwas hängt,
 * wird mit Grund abgewiesen**, statt ihre Konten und Positionen wie in den
 * Stammdaten umzuhängen: Umgehängt wird im Bericht mit *Ausschneiden*.
 */

export type Loeschziel = {
  art: 'position' | 'konto'
  schluessel: string
  name: string
  /**
   * Was mit dem Element verschwindet, wenn es restlos entfernt wird — die
   * Zuordnung, mit der ein Statistikkonto unter seiner Position steht (2.1
   * Abschnitt 3.6, R1727). Wird es nur stillgelegt, bleibt sie: Seine alten
   * Werte stehen weiter unter der Position.
   */
  mitnehmen?: { objektart: string; schluessel: string }
}

/** Das Ereignis, mit dem ein Menü das Löschfenster ruft. */
export const LOESCHEN = 'coko:element-loeschen'

let erlaubt = false

/**
 * Ob der Anwender löschen darf (R1180: Was er nicht darf, ist auf keiner Seite
 * da). Das Fenster setzt es aus den Rechten — das Menü ist keine Komponente
 * und kann sie selbst nicht fragen.
 */
export function loeschenErlaubt(): boolean {
  return erlaubt
}

export function setzeLoescherlaubnis(darf: boolean): void {
  erlaubt = darf
}

/** Ruft das Löschfenster für dieses Element. */
export function rufeLoeschen(ziel: Loeschziel): void {
  window.dispatchEvent(new CustomEvent<Loeschziel>(LOESCHEN, { detail: ziel }))
}

/** Wie viele Elemente unter einer Position hängen — Konten und Positionen zusammen. */
export function haengtDarunter(lage: Loeschlage): number {
  return lage.kinder.reduce((summe, v) => summe + v.anzahl, 0)
}

export function Elementloeschfenster({ aufGeloescht }: { aufGeloescht: () => void }) {
  const darf = useDarf()
  const darfLoeschen = darf('aussondern')
  const [ziel, setZiel] = useState<Loeschziel | null>(null)
  const [lage, setLage] = useState<Loeschlage | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    setzeLoescherlaubnis(darfLoeschen)
  }, [darfLoeschen])

  useEffect(() => {
    const ruf = (e: Event) => {
      const neu = (e as CustomEvent<Loeschziel>).detail
      setZiel(neu)
      setLage(null)
      setMeldung(null)
      api
        .loeschlage(neu.art, neu.schluessel)
        .then(setLage)
        .catch((f: unknown) => setMeldung(fachlicherSatz(f)))
    }
    window.addEventListener(LOESCHEN, ruf)
    return () => window.removeEventListener(LOESCHEN, ruf)
  }, [])

  if (ziel === null) {
    return null
  }

  const schliessen = () => setZiel(null)
  const titel = t(ziel.art === 'position' ? 'anlage.menue.loeschen.position' : 'anlage.menue.loeschen.konto').replace(
    / …$/,
    '',
  )

  if (meldung !== null) {
    return <Hinweisfenster titel={titel} meldung={meldung} aufSchliessen={schliessen} />
  }

  // **Unter ihr hängt etwas — abgewiesen, mit dem Grund** (R1707).
  if (lage !== null && haengtDarunter(lage) > 0) {
    return (
      <Hinweisfenster
        titel={titel}
        meldung={
          haengtDarunter(lage) === 1
            ? t('loeschen.element.darunter.eins', { name: ziel.name })
            : t('loeschen.element.darunter', { name: ziel.name, anzahl: anzahlwort(haengtDarunter(lage)) })
        }
        aufSchliessen={schliessen}
      />
    )
  }

  async function loeschen(auftrag: Loeschauftrag) {
    if (ziel === null) {
      return
    }
    setLaeuft(true)
    try {
      const ergebnis = await api.aussondern(ziel.art, ziel.schluessel, auftrag)
      if (ergebnis.art === 'entfernt' && ziel.mitnehmen) {
        await api.aussondern(ziel.mitnehmen.objektart, ziel.mitnehmen.schluessel, {
          weg: 'entfernen',
          abPeriode: null,
          ziel: null,
        })
      }
      meldeErfolg(t(ergebnis.art === 'entfernt' ? 'erfolg.stammdaten.entfernt' : 'erfolg.stammdaten.stillgelegt'))
      aufGeloescht()
      setZiel(null)
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Loeschfenster
      lage={lage}
      code={ziel.name}
      vorrat={[]}
      laeuft={laeuft}
      aufLoeschen={(auftrag) => void loeschen(auftrag)}
      aufSchliessen={schliessen}
    />
  )
}
